import type { Dispatch, SetStateAction } from 'react'

import type { PaginationState } from '@tanstack/react-table'

import LeftChevronIcon from '@/assets/icons/icon-chevron-left.svg'
import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TimelineData } from '@/modules/chatting/ui/chat-window/fan-info/ppv-stats/ppv-timeline/types'

const paginationOptions = [10, 20, 30, 40]

interface Props {
  pagination: PaginationState
  pageCount: number
  setPagination: Dispatch<SetStateAction<PaginationState>>
  data: TimelineData[]
}

export const ChartPagination = ({
  pagination,
  pageCount,
  setPagination,
  data,
}: Props) => {
  const { pageSize, pageIndex } = pagination

  const handleSelectChange = (value: string) => {
    const pageSize = Number(value)
    const newPageCount = Math.ceil(data.length / pageSize)
    const newPageIndex = Math.min(pageIndex, newPageCount - 1)

    setPagination((prev) => ({ ...prev, pageSize, pageIndex: newPageIndex }))
  }

  const handlePreviousButtonClick = () => {
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }))
  }

  const handleNextButtonClick = () => {
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }))
  }

  return (
    <div className="mt-6 flex w-full flex-col items-center justify-between gap-2 sm:flex-row">
      <div className="flex items-center gap-4 text-sm">
        <div className="text-secondary-500 text-slate-900 dark:text-white">
          Media per page
        </div>
        <Select
          onValueChange={handleSelectChange}
          value={String(pageSize)}
        >
          <SelectTrigger className="w-[100px] outline-none">
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent>
            {paginationOptions.map((pageSize) => (
              <SelectItem
                key={pageSize}
                value={String(pageSize)}
              >
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="text-sm dark:text-white">
          Page {pageIndex + 1} of {pageCount}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          disabled={!pageIndex}
          variant="outline"
          onClick={handlePreviousButtonClick}
        >
          <LeftChevronIcon className="fill-secondary-500" />
          Previous
        </Button>
        <Button
          variant="outline"
          onClick={handleNextButtonClick}
          disabled={pageIndex === pageCount - 1}
        >
          Next
          <RightChevronIcon className="fill-secondary-500" />
        </Button>
      </div>
    </div>
  )
}
